import { openDB, IDBPDatabase } from 'idb';
import { showToast } from '#root/components/GlobalToaster';
import { OFFLINE_MESSAGES } from '#root/utils/offline';
import { getCurrentUserFromStorage, getCurrentTenantFromStorage } from '#root/queries/local';
import axios from 'axios';
import { executeOperation, Operations } from '#root/utils/offlineOperationHandlers';
import { OfflineOperationType } from '#root/utils/offlineOperations';
import { ROUTES } from '#root/routes/constants';

// Constants
const FUNCTION_URL = process.env.REACT_APP_FUNCTION_URL;
const NOTIFICATION_THROTTLE_KEY = 'EMC_SERVER_DOWN_NOTIFICATION_SENT';
const NOTIFICATION_THROTTLE_DURATION_MS = 600000; // 10 minutes
const IS_LOCAL_ENVIRONMENT = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const DB_CONSTANTS = {
    NAME: 'EMC_OFFLINE_OPERATIONS_DB',
    VERSION: 1,
    STORES: {
        OFFLINE_QUEUE: 'offlineQueue'
    }
} as const;

const SYNC_INTERVAL_MS = 30000; // 30 seconds
const RETRY_DELAY_MS = 10000;   // 10 seconds
const ONLINE_DEBOUNCE_MS = 5000; // 5 seconds

// Types
export interface OfflineOperation<T = any> {
    id: string;
    timestamp: number;
    operationType: OfflineOperationType;
    operationData: T;
    retryCount: number;
    lastRetryTimestamp?: number;
    tenantId: string | null;
    userId: string | null;
    metadata?: Record<string, any>;
}

interface SyncResult {
    hasSuccessfulSync: boolean;
    hasFailedSync: boolean;
    hasInvalidTenantSync: boolean;
    successCount: number;
}

type OfflineMode = 'none' | 'network' | 'server';

export class OfflineOperationsService {
    private static instance: OfflineOperationsService;
    private db: IDBPDatabase | null = null;
    private onlineDebounceTimeout: number | null = null;
    private syncInterval: number | null = null;
    private offlineMode: OfflineMode = 'none';
    private operations: Operations | null = null;

    private PROTECTED_PATHS = [
        ROUTES.VTRACKER.ADD_VICTIM,
        ROUTES.VTRACKER.ADD_CONTACT,
        ROUTES.SERVER_DOWN
    ];

    private constructor() {
        this.initializeEventListeners();
        // Initialize immediately when instance is created
        this.init().catch(error => {
            console.error('[OfflineOperationsService] Failed to initialize:', error);
        });
    }

    public static getInstance(): OfflineOperationsService {
        if (!OfflineOperationsService.instance) {
            OfflineOperationsService.instance = new OfflineOperationsService();
        }
        return OfflineOperationsService.instance;
    }

    public setOperations(operations: Operations): void {
        this.operations = operations;
    }

    // Event Listeners
    private initializeEventListeners(): void {
        window.addEventListener('online', this.handleNetworkOnline);
        window.addEventListener('offline', this.handleNetworkOffline);
        window.addEventListener('unload', this.cleanup);
    }

    // Online/Offline Network handlers
    private handleNetworkOffline = (): void => {
        console.log('[OfflineOperationsService] Network went offline');
        showToast(OFFLINE_MESSAGES.WENT_OFFLINE, 'warning');
        
        this.clearTimeouts();
        this.stopSyncInterval();
        this.offlineMode = 'network';
    };

    private handleNetworkOnline = (): void => {
        if (this.onlineDebounceTimeout) {
            window.clearTimeout(this.onlineDebounceTimeout);
        }

        this.onlineDebounceTimeout = window.setTimeout(async () => {
            this.offlineMode = 'none';
            console.log('[OfflineOperationsService] Network back online.');
            await this.processQueue();
            this.onlineDebounceTimeout = null;
        }, ONLINE_DEBOUNCE_MS);
    };

    // Online/Offline Server public handlers
    // public handleServerOffline(): void {
    //     if (this.offlineMode === 'none') {
    //         this.offlineMode = 'server';
    //         this.startSyncInterval();
    //     }
    // }

    public handleServerOffline(): void {
        // Don't handle server offline if we're already in network offline mode
        if (this.offlineMode === 'network') {
            return;
        }

        // Set offline mode and start sync interval regardless of path
        this.offlineMode = 'server';
        this.startSyncInterval();

        // Trigger Azure Function to notify about API being offline
        this.notifySuperAdminAboutServerDown();

        // Check if we're on a protected form path
        const currentPath = window.location.pathname;
        const isProtectedForm = this.PROTECTED_PATHS.some(path => 
            currentPath.startsWith(path)
        );

        // Only redirect if not on a protected path
        if (!isProtectedForm) {
            window.location.href = ROUTES.SERVER_DOWN;
        }
    };

    private async notifySuperAdminAboutServerDown(): Promise<void> {
        if (IS_LOCAL_ENVIRONMENT) {
            console.log('[OfflineOperationsService] Notification skipped in local environment');
            return;
        }
        
        const lastNotificationTime = localStorage.getItem(NOTIFICATION_THROTTLE_KEY);
        if (lastNotificationTime) {
            const timeSinceLastNotification = Date.now() - parseInt(lastNotificationTime, 10);
            if (timeSinceLastNotification < NOTIFICATION_THROTTLE_DURATION_MS) {
                console.log('[OfflineOperationsService] Notification throttled, last sent', new Date(parseInt(lastNotificationTime, 10)));
                return;
            }
        }

        if (!FUNCTION_URL) {
            console.error('[OfflineOperationsService] Function URL not configured');
            return;
        }

        try {
            await axios.post(FUNCTION_URL);
            console.log('[OfflineOperationsService] Super admin notification sent successfully');
        } catch (error) {
            console.error('[OfflineOperationsService] Failed to send super admin notification:', error);
        }
        
        localStorage.setItem(NOTIFICATION_THROTTLE_KEY, Date.now().toString());
    };

    public handleServerOnline(): void {
        if (this.offlineMode === 'server') {
            this.offlineMode = 'none';
            this.processQueue();
        }
    }

    // Sync Interval Management (Server)
    private startSyncInterval(): void {
        if (this.syncInterval) return;

        this.syncInterval = window.setInterval(() => {
            console.log('[OfflineOperationsService] Attempting sync after server down');
            this.processQueue();
        }, SYNC_INTERVAL_MS);
    }

    private stopSyncInterval(): void {
        if (!this.syncInterval) return;
        
        window.clearInterval(this.syncInterval);
        this.syncInterval = null;
    }

    // Database Operations
    async init(): Promise<IDBPDatabase> {
        if (this.db) return this.db;

        this.db = await openDB(DB_CONSTANTS.NAME, DB_CONSTANTS.VERSION, {
            upgrade: this.handleDatabaseUpgrade
        });

        console.log('[OfflineOperationsService] Service initialized')
        await this.checkAndProcessPendingOperations();
        return this.db;
    }

    private handleDatabaseUpgrade(db: IDBPDatabase): void {
        if (!db.objectStoreNames.contains(DB_CONSTANTS.STORES.OFFLINE_QUEUE)) {
            const store = db.createObjectStore(DB_CONSTANTS.STORES.OFFLINE_QUEUE, { keyPath: 'id' });
            store.createIndex('timestamp', 'timestamp');
            store.createIndex('lastRetryTimestamp', 'lastRetryTimestamp');
            store.createIndex('tenantId', 'tenantId');
            store.createIndex('userId', 'userId');
        }
    }

    public async queueOperation<T>(operationType: OfflineOperationType, operationData: any, metadata?: Record<string, any>): Promise<void> {
        const tenantId = this.getCurrentTenantId();
        const userId = this.getCurrentUserId();

        if (!userId) {
            throw new Error('User ID is required to queue operations');
        }

        const offlineOperation: OfflineOperation<T> = {
            id: crypto.randomUUID(),
            timestamp: Date.now(),
            operationType,
            operationData,
            retryCount: 0,
            lastRetryTimestamp: 0,
            tenantId,
            userId,
            metadata
        };

        await this.db!.add(DB_CONSTANTS.STORES.OFFLINE_QUEUE, offlineOperation);
    }

    // Queue Processing
    private async checkAndProcessPendingOperations(): Promise<void> {
        const operations = await this.getPendingOperations();
        if (operations.length > 0 && navigator.onLine && this.offlineMode === 'none') {
            console.log(`[OfflineOperationsService] Found ${operations.length} pending operations, processing immediately`);
            await this.processQueue();
        }
    }

    async getPendingOperations(): Promise<OfflineOperation[]> {
        const db = await this.init();
        const currentTenantId = this.getCurrentTenantId();
        const currentUserId = this.getCurrentUserId();
        const allOperations = await db.getAllFromIndex(DB_CONSTANTS.STORES.OFFLINE_QUEUE, 'timestamp');
        return allOperations.filter(operation => operation.tenantId === currentTenantId && operation.userId === currentUserId);
    }

    private canProcessQueue(): boolean {
        if (!this.db) {
            return false;
        }

        if (this.offlineMode === 'network') {
            return false;
        }

        return true;
    }

    public processQueue = async (): Promise<void> => {
        if (!this.canProcessQueue()) return;

        const matchingOperations = await this.getPendingOperations();
        
        if (matchingOperations.length === 0) {
            this.handleEmptyQueue();
            return;
        }

        console.log(`[OfflineOperationsService] Processing ${matchingOperations.length} pending operations`);
        const result = await this.processOperations(matchingOperations);
        await this.handleProcessingComplete(result);
    };

    private async processOperations(operations: OfflineOperation[]): Promise<SyncResult> {
        const result: SyncResult = {
            hasSuccessfulSync: false,
            hasFailedSync: false,
            hasInvalidTenantSync: false,
            successCount: 0
        };

        for (const operation of operations) {
            if (await this.shouldSkipOperation(operation, result)) continue;
            
            try {
                await this.processOperation(operation, result);
            } catch (error) {
                // For network errors, pause processing the queue
                if (axios.isAxiosError(error) && (error.code === 'ERR_NETWORK' || !error.response)) {
                    console.log('[OfflineOperationsService] Network error, pausing queue processing');
                    this.handleServerOffline();
                    break;
                }
                
                // For other errors, continue with next operation
                result.hasFailedSync = true;
            }
        }

        return result;
    }

    private async processOperation(operation: OfflineOperation, result: SyncResult): Promise<void> {
        try {
            if (!this.operations) {
                throw new Error('Operations not initialized');
            }
            
            await executeOperation(operation.operationType, operation.operationData, this.operations);
            await this.handleOperationSuccess(operation, result);
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                // Log axios error details
                console.error('[OfflineOperationsService] Axios error:', {
                    operationId: operation.id,
                    operationType: operation.operationType,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    errorMessage: error.message,
                    errorCode: error.code,
                    errorData: error.response?.data
                });

                // For network errors, just re-throw to stop queue processing
                if (error.code === 'ERR_NETWORK' || !error.response) {
                    throw error;
                }
                
                // For auth errors, keep in queue but don't increment retry count
                if (error.response?.status === 401) {
                    operation.lastRetryTimestamp = Date.now();
                    await this.db!.put(DB_CONSTANTS.STORES.OFFLINE_QUEUE, operation);
                } else {
                    // For other errors (4xx/5xx), remove from queue
                    await this.handleNonRetryableError(operation);
                }
            } else if (!(error instanceof axios.Cancel)) {
                // Log non-axios error details
                console.error('[OfflineOperationsService] Non-axios error:', {
                    operationId: operation.id,
                    operationType: operation.operationType,
                    errorName: error.name,
                    errorMessage: error.message,
                    errorStack: error.stack
                });
                await this.handleNonAxiosError(operation);
            }
            result.hasFailedSync = true;
        }
    }

    private async shouldSkipOperation(
        operation: OfflineOperation, 
        result: SyncResult
    ): Promise<boolean> {
        const now = Date.now();
        if (operation.lastRetryTimestamp && (now - operation.lastRetryTimestamp) < RETRY_DELAY_MS) {
            result.hasFailedSync = true;
            return true;
        }

        return false;
    }

    private handleEmptyQueue(): void {
        //console.log('[OfflineService] No requests in queue, service paused');
        this.stopSyncInterval();
    }

    // Queue processing complete
    private async handleProcessingComplete(result: SyncResult): Promise<void> {
        const pendingOperations = await this.getPendingOperations();
        
        if (pendingOperations.length === 0) {
            console.log('[OfflineOperationsService] All operations for current user processed, stopping sync');
            this.offlineMode = 'none';
            this.stopSyncInterval();
        }

        this.showSyncResults(result);
    }

    private showSyncResults(result: SyncResult): void {        
        // Show sync success when everything is synced
        if (result.successCount > 0 && !result.hasFailedSync) {
            showToast(OFFLINE_MESSAGES.SYNC_SUCCESS, 'success');
        }
        
        // Show tenant mismatch warnings
        if (result.hasInvalidTenantSync) {
            console.log('[OfflineOperationsService] Some operations were skipped due to tenant/user mismatch');
        }
    }

    private async handleOperationSuccess(
        operation: OfflineOperation,
        result: SyncResult
    ): Promise<void> {
        await this.db!.delete(DB_CONSTANTS.STORES.OFFLINE_QUEUE, operation.id);
        result.hasSuccessfulSync = true;
        result.successCount++;
        this.offlineMode = 'none';
    }

    private async handleNonRetryableError(operation: OfflineOperation): Promise<void> {
        await this.removeFromQueue(operation, 'Non-retryable error');
    }

    private async handleNonAxiosError(operation: OfflineOperation): Promise<void> {
        await this.removeFromQueue(operation, 'Non-Axios error');
    }

    private async removeFromQueue(operation: OfflineOperation, reason: string): Promise<void> {
        await this.db!.delete(DB_CONSTANTS.STORES.OFFLINE_QUEUE, operation.id);
        console.log(`[OfflineOperationsService] ${reason}, removing operation ${operation.id} from queue`);
    }

    // Utility Methods
    private getCurrentTenantId(): string | null {
        return getCurrentTenantFromStorage();
    }

    private getCurrentUserId(): string | null {
        const user = getCurrentUserFromStorage();
        return user?.id ?? null;
    }

    async clearQueue(): Promise<void> {
        const db = await this.init();
        await db.clear(DB_CONSTANTS.STORES.OFFLINE_QUEUE);
    }

    private cleanup = (): void => {
        this.clearTimeouts();
    };

    private clearTimeouts(): void {
        if (this.onlineDebounceTimeout) {
            window.clearTimeout(this.onlineDebounceTimeout);
            this.onlineDebounceTimeout = null;
        }
        if (this.syncInterval) {
            window.clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }
}

// Export singleton instance
export const offlineOperationsService = OfflineOperationsService.getInstance();
